import { login as APILogin } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import { Spinner } from "@/components/ui/spinner";
import { useAuth } from "@/contexts/auth/useAuth";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { parseAuthError } from "@/utils/auth-errors";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router";

type FormData = {
    email: string;
    password: string;
};

const Login = () => {
    const { session, authenticated, user } = useAuth();
    const navigate = useNavigate();

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<FormData>({ defaultValues: { email: "", password: "" } });

    const { mutate, isPending, error } = useMutation({
        mutationFn: (data: FormData) => APILogin(data.email, data.password),
        onSuccess: (data) => {
            session.create(data.access_token, data.refresh_token);
        },
    });

    const serverError = useMemo(
        () => (error ? parseAuthError(error) : null),
        [error]
    );

    useDocumentTitle("Sign in");

    useEffect(() => {
        if (authenticated && user) {
            navigate(user.is_admin ? "/admin" : "/");
        }
    }, [authenticated, navigate, user]);

    return (
        <form onSubmit={handleSubmit((data) => mutate(data))} noValidate>
            <div className="space-y-7">
                {/* Header */}
                <div className="space-y-2">
                    <h1 className="text-3xl font-semibold tracking-tight">
                        Sign in
                    </h1>
                    <p className="text-muted-foreground text-sm">
                        Pick up where you left off.
                    </p>
                </div>

                {/* Server error */}
                {serverError && (
                    <div
                        role="alert"
                        aria-live="polite"
                        className={
                            serverError.snackBarVariant === "warning"
                                ? "border-destructive/25 bg-destructive/[0.04] text-foreground rounded-md border px-3 py-2.5 text-sm"
                                : "border-destructive/30 bg-destructive/[0.06] text-destructive rounded-md border px-3 py-2.5 text-sm"
                        }
                    >
                        {serverError.message}
                    </div>
                )}

                {/* Fields */}
                <div className="space-y-4">
                    <div className="space-y-1.5">
                        <Label htmlFor="email">Email</Label>
                        <Input
                            id="email"
                            type="email"
                            className="h-10"
                            autoComplete="email"
                            autoFocus
                            aria-invalid={!!errors.email}
                            {...register("email", {
                                required: "Email is required",
                                pattern: {
                                    value: /^\S+@\S+\.\S+$/,
                                    message: "Enter a valid email",
                                },
                            })}
                        />
                        {errors.email && (
                            <p className="text-destructive text-sm">
                                {errors.email.message}
                            </p>
                        )}
                    </div>

                    <div className="space-y-1.5">
                        <div className="flex items-baseline justify-between">
                            <Label htmlFor="password">Password</Label>
                            <Link
                                to="/password/forgot"
                                className="text-primary text-xs font-medium hover:underline"
                            >
                                Forgot password?
                            </Link>
                        </div>
                        <PasswordInput
                            id="password"
                            autoComplete="current-password"
                            aria-invalid={!!errors.password}
                            {...register("password", {
                                required: "Password is required",
                            })}
                        />
                        {errors.password && (
                            <p className="text-destructive text-sm">
                                {errors.password.message}
                            </p>
                        )}
                    </div>
                </div>

                {/* Submit */}
                <Button
                    type="submit"
                    disabled={isPending}
                    className="h-10 w-full"
                >
                    {isPending && <Spinner className="mr-2" />}
                    Sign in
                </Button>
            </div>
        </form>
    );
};

export default Login;
